import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { ExperimentPostSurveyForm } from "../components/ExperimentPostSurveyForm";
import { Button } from "../components/ui/button";
import { AuthService } from "../lib/auth";
import type { AuthStatus, UserProfile } from "../types";

export default function ExperimentPostSurvey() {
  const [, setLocation] = useLocation();
  const authService = AuthService.getInstance();
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const rawInterviewId = params.get("interview_id");
  const interviewId = rawInterviewId ? Number.parseInt(rawInterviewId, 10) : NaN;

  const { data: authStatus, isLoading: authLoading } = useQuery<AuthStatus>({
    queryKey: ["authStatus"],
    queryFn: () => authService.getAuthStatus(),
    staleTime: 5 * 60 * 1000,
  });
  const { data: currentUser } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => authService.getCurrentUser(),
    enabled: authStatus?.isAuthenticated === true,
    staleTime: 5 * 60 * 1000,
  });

  if (authLoading) {
    return (
      <SurveyPageShell>
        <div className="flex min-h-56 items-center justify-center gap-2 text-sm font-bold text-zinc-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          画面を準備しています
        </div>
      </SurveyPageShell>
    );
  }

  if (authStatus?.authMode !== "experiment") {
    return (
      <SurveyPageShell>
        <MessageBlock message="このアンケートは現在のログイン方法では利用できません。" />
      </SurveyPageShell>
    );
  }

  if (!Number.isInteger(interviewId) || interviewId <= 0) {
    return (
      <SurveyPageShell>
        <MessageBlock message="面接IDが指定されていません。評価結果から開き直してください。" />
      </SurveyPageShell>
    );
  }

  const participantCode = currentUser?.participantCode ?? authStatus.user?.participantCode;

  return (
    <SurveyPageShell>
      <ExperimentPostSurveyForm
        interviewId={interviewId}
        participantCode={participantCode}
      />
      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={() => setLocation(`/evaluation?interview_id=${interviewId}`)}>
          <ArrowLeft />
          評価結果へ戻る
        </Button>
      </div>
    </SurveyPageShell>
  );
}

function SurveyPageShell({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-screen bg-zinc-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-5xl">{children}</div>
    </main>
  );
}

function MessageBlock({ message }: { message: string }) {
  return (
    <div className="border border-zinc-200 bg-white px-5 py-8 text-center">
      <h1 className="text-xl font-bold text-zinc-950">面接後アンケート</h1>
      <p className="mt-3 text-sm leading-6 text-zinc-600">{message}</p>
    </div>
  );
}
